import type { ColDef } from "ag-grid-community";
import { makeAutoObservable } from "mobx";
import { makeStoreProvider } from "@synthesis/shared-utils";

import { RoadIntakeCell } from "./local-components/road-intake-cell";
import { RoadProcessesCell } from "./local-components/road-processes-cell";

export type ConstructorRoad = {
  id: string;
  name: string;
  code: string;
  /** Процессы: «2 своя версии» (синий) или «Общие» (серый). */
  processes: { label: string; isConfigured: boolean };
  /** Исполнитель входного контроля: настроен (пользователь/роль) или «Не назначен». */
  intakeLabel: string;
  intakeIsConfigured: boolean;
  departmentsCount: number | null;
  usersCount: number | null;
};

/** Форма дороги (модалки добавления и изменения). */
export type RoadFormValues = {
  name: string;
  code: string;
  shortName: string;
  administrationCode: string;
};

type RailwayOption = { value: string; label: string };

type RoadExtra = Pick<ConstructorRoad, "processes" | "intakeLabel" | "intakeIsConfigured" | "departmentsCount" | "usersCount">;

const DEFAULT_ROAD_EXTRA: RoadExtra = {
  processes: { label: "Общие", isConfigured: false },
  intakeLabel: "Не назначен",
  intakeIsConfigured: false,
  departmentsCount: null,
  usersCount: null,
};

/** Настройки по макету для отдельных дорог (справочника) — процесс/входной контроль/сквозные счётчики. */
const ROAD_EXTRA_BY_CODE: Record<string, RoadExtra> = {
  OKTYABRSKAYA: {
    processes: { label: "2 своя версии", isConfigured: true },
    intakeLabel: "2 пользователя",
    intakeIsConfigured: true,
    departmentsCount: 4,
    usersCount: 7,
  },
  MOSKOVSKAYA: {
    processes: { label: "1 своя версия", isConfigured: true },
    intakeLabel: "Оператор (внутреннее согласование)",
    intakeIsConfigured: true,
    departmentsCount: 3,
    usersCount: 6,
  },
  GORKOVSKAYA: {
    processes: { label: "1 своя версия", isConfigured: true },
    intakeLabel: "1 пользователь",
    intakeIsConfigured: true,
    departmentsCount: 2,
    usersCount: 4,
  },
};

/**
 * Список дорог для таблицы «Конструктор — Дороги» строится из базового справочника
 * (та же ручка, что в форме создания заявки). Остальные колонки — мок-атрибуты.
 */
export const buildConstructorRoads = (railways: RailwayOption[]): ConstructorRoad[] =>
  railways.map((railway) => ({
    id: railway.value,
    name: railway.label,
    code: railway.value,
    ...(ROAD_EXTRA_BY_CODE[railway.value] ?? DEFAULT_ROAD_EXTRA),
  }));

/** Фолбэк, если справочник недоступен (по мотивам макета «Конструктор - Дороги»). */
export const MOCK_CONSTRUCTOR_ROADS: ConstructorRoad[] = [
  {
    id: "OKTYABRSKAYA",
    name: "Октябрьская железная дорога",
    code: "OKTYABRSKAYA",
    processes: { label: "2 своя версии", isConfigured: true },
    intakeLabel: "2 пользователя",
    intakeIsConfigured: true,
    departmentsCount: 4,
    usersCount: 7,
  },
  {
    id: "KALININGRADSKAYA",
    name: "Калининградская железная дорога",
    code: "KALININGRADSKAYA",
    processes: { label: "Общие", isConfigured: false },
    intakeLabel: "Не назначен",
    intakeIsConfigured: false,
    departmentsCount: null,
    usersCount: null,
  },
  {
    id: "MOSKOVSKAYA",
    name: "Московская железная дорога",
    code: "MOSKOVSKAYA",
    processes: { label: "1 своя версия", isConfigured: true },
    intakeLabel: "Оператор (внутреннее согласование)",
    intakeIsConfigured: true,
    departmentsCount: 3,
    usersCount: 6,
  },
  {
    id: "GORKOVSKAYA",
    name: "Горьковская железная дорога",
    code: "GORKOVSKAYA",
    processes: { label: "1 своя версия", isConfigured: true },
    intakeLabel: "1 пользователь",
    intakeIsConfigured: true,
    departmentsCount: 2,
    usersCount: 4,
  },
];

// ---------------------------------- COLUMNS ----------------------------------

export const ROAD_COLUMNS: ColDef<ConstructorRoad>[] = [
  { colId: "name", headerName: "Дорога", field: "name", width: 260 },
  { colId: "code", headerName: "Код", field: "code", width: 140 },
  { colId: "processes", headerName: "Процессы", width: 210, cellRenderer: RoadProcessesCell },
  { colId: "intake", headerName: "Входной контроль", width: 300, cellRenderer: RoadIntakeCell },
  { colId: "departments", headerName: "Подразделения", field: "departmentsCount", width: 170, valueFormatter: ({ value }) => value ?? "—" },
  { colId: "users", headerName: "Пользователи", field: "usersCount", width: 170, valueFormatter: ({ value }) => value ?? "—" },
];

// ---------------------------------- STORE ----------------------------------

export type RoadModalAction = { type: "add" } | { type: "edit"; road: ConstructorRoad };

class PageStore {
  public popupAction: RoadModalAction | null = null;

  constructor() {
    makeAutoObservable(this);
  }

  public setPopupAction(action: RoadModalAction | null) {
    this.popupAction = action;
  }
}

const { StoreProvider: PageStoreProvider, useStore: usePageStore } = makeStoreProvider(PageStore);

export { PageStoreProvider, usePageStore };
